import { useEffect, useState } from 'react';
import { timesheetApi } from '@/services/timesheetApi';
import { employeesApi } from '@/services/employeesApi';

export interface ReportFilterOptions {
  employees: { id: number; name: string }[];
  departments: string[];
  projects: { id: string; code: string; name: string }[];
  costCentres: { code: string; name: string }[];
}

const EMPTY_OPTIONS: ReportFilterOptions = { employees: [], departments: [], projects: [], costCentres: [] };

const valueOrEmpty = <T,>(result: PromiseSettledResult<T[]>): T[] =>
  result.status === 'fulfilled' && Array.isArray(result.value) ? result.value : [];

/**
 * Loads report filter options (employees, departments, projects and optionally cost centres)
 * from the existing lookup endpoints. Any lookup that fails (e.g. the caller lacks access)
 * falls back to an empty list so the report itself still loads.
 */
export function useReportFilterOptions(
  companyId: string | undefined,
  { enabled = true, includeCostCentres = false }: { enabled?: boolean; includeCostCentres?: boolean } = {},
): ReportFilterOptions {
  const [options, setOptions] = useState<ReportFilterOptions>(EMPTY_OPTIONS);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    Promise.allSettled([
      timesheetApi.getAllEmployees(),
      employeesApi.getDirectorySummary().then((s) => s.departments),
      timesheetApi.getAllProjects(companyId),
      includeCostCentres ? timesheetApi.getAllCostCentres(companyId) : Promise.resolve([]),
    ]).then(([employees, departments, projects, costCentres]) => {
      if (cancelled) return;
      setOptions({
        employees: valueOrEmpty(employees)
          .map((e) => ({ id: e.id, name: e.name }))
          .sort((a, b) => a.name.localeCompare(b.name)),
        departments: [...new Set(valueOrEmpty(departments).map((d) => d.value).filter(Boolean))].sort(),
        projects: valueOrEmpty(projects).map((p) => ({ id: p.id, code: p.code, name: p.name })),
        costCentres: [...new Map(valueOrEmpty(costCentres).map((c) => [c.code, { code: c.code, name: c.name }])).values()],
      });
    });

    return () => { cancelled = true; };
  }, [companyId, enabled, includeCostCentres]);

  return options;
}
